import { CheckedAddress } from '@sparkdotfi/common-universal'
import { useSearch } from '@tanstack/react-router'
// eslint-disable-next-line no-restricted-imports
import { useAccount, useChainId } from 'wagmi'

import { NotConnectedError } from '@/domain/errors/not-connected'

export type WalletMode = 'connected' | 'watch-wallet'

export type ConnectedInfo<T extends boolean> = T extends true
  ? {
      chainId: number
      account: CheckedAddress
      connector: ReturnType<typeof useAccount>['connector']
      isConnecting: boolean
      walletMode: WalletMode
    }
  : {
      chainId: number
      account: CheckedAddress | undefined
      connector: ReturnType<typeof useAccount>['connector']
      isConnecting: boolean
      walletMode: WalletMode
    }

function getWalletMode(watchAddress: CheckedAddress | undefined): WalletMode {
  return watchAddress ? 'watch-wallet' : 'connected'
}

export function useConnectedAddress<T extends true | false = true>({
  throwOnNotConnected,
}: {
  throwOnNotConnected?: T
} = {}): ConnectedInfo<T> {
  const { address, connector, isConnecting } = useAccount()
  const chainId = useChainId()
  const { 'watch-address': watchAddress } = useSearch({ strict: false })

  const walletMode = getWalletMode(watchAddress)
  const effectiveAddress = watchAddress ?? address

  if (throwOnNotConnected === undefined || throwOnNotConnected) {
    if (!effectiveAddress) throw new NotConnectedError()
    return {
      chainId,
      account: CheckedAddress(effectiveAddress),
      connector,
      isConnecting,
      walletMode,
    }
  }

  return {
    chainId,
    account: effectiveAddress && CheckedAddress(effectiveAddress),
    connector,
    isConnecting,
    walletMode,
  } as ConnectedInfo<T>
}
